import React from 'react';
import { ArrowLeft, User } from 'lucide-react';

interface Message {
  message_id: string;
  timestamp: string;
  display_time: string;
  role: string;
  content: string;
}

interface PersonaChange {
  previous_persona: string;
  new_persona: string;
  timestamp: string;
}

interface ChatDetailProps {
  chatData: {
    chat_id: string;
    timestamp: string;
    summary: string;
    persona: string | null;
    messages: Message[];
    persona_changes?: PersonaChange[];
  };
  onBack: () => void;
}

const ChatDetail: React.FC<ChatDetailProps> = ({ chatData, onBack }) => {
  if (!chatData) {
    return <div className="p-6 text-center">No chat data available</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header with back button and summary */}
      <div className="bg-gray-50 p-4 border-b">
        <div className="flex items-center mb-4">
          <button 
            onClick={onBack}
            className="mr-2 p-1 rounded-full hover:bg-gray-200 transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft size={20} />
          </button>
          <h2 className="text-xl font-semibold">Chat Detail</h2>
        </div>
        
        <div className="flex items-center mb-3">
          <User size={16} className="mr-2 text-blue-500" />
          <span className="text-sm font-medium">{chatData.persona || 'Unknown'}</span>
          <span className="mx-2 text-gray-400">•</span>
          <span className="text-sm text-gray-500">
            {new Date(chatData.timestamp).toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </span>
        </div>
        
        <div className="bg-blue-50 p-3 rounded-md">
          <h3 className="text-sm font-semibold text-blue-700 mb-1">Summary</h3>
          <p className="text-sm text-blue-800">{chatData.summary}</p>
        </div>
      </div>
      
      {/* Chat messages */}
      <div className="p-4 space-y-4 max-h-[calc(100vh-280px)] overflow-y-auto">
        {chatData.persona_changes && chatData.persona_changes.length > 0 && (
          <div className="bg-yellow-50 p-3 rounded-md mb-4 text-sm text-yellow-800">
            <p className="font-medium">Persona switches detected in this conversation:</p>
            <ul className="list-disc pl-5 mt-1">
              {chatData.persona_changes.map((change, idx) => (
                <li key={idx}>
                  Changed from <span className="font-medium">{change.previous_persona || 'Unknown'}</span> to <span className="font-medium">{change.new_persona}</span>
                  <span className="text-xs text-gray-500 ml-2">
                    ({new Date(change.timestamp).toLocaleTimeString([], {hour: 'numeric', minute:'2-digit'})})
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {chatData.messages.map(message => (
          <div 
            key={message.message_id}
            className={`flex ${message.role === 'assistant' ? 'justify-start' : 'justify-end'}`}
          >
            <div className={`
              max-w-3/4 p-3 rounded-lg 
              ${message.role === 'assistant' 
                ? 'bg-blue-50 text-blue-900' 
                : 'bg-gray-100 text-gray-900 ml-auto'
              }
            `}>
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-medium">
                  {message.role === 'assistant' ? chatData.persona || 'Assistant' : 'User'}
                </span>
                <span className="text-xs text-gray-500 ml-2">
                  {message.display_time}
                </span>
              </div>
              <div className="whitespace-pre-wrap">
                {message.content}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChatDetail;
